// Create / edit a team. Controlled fields (plain useState, as in solya-app's
// settings dialogs). On both create and edit: name + description. The owning org
// is fixed (the active org on create, immutable on edit).

import { useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { TeamDetailDto, TeamDto } from "../api/types.js"
import { fr } from "../i18n/fr.js"
import { isDuplicateError, messageForError } from "../lib/errors.js"
import { Drawer } from "../ui/Drawer.js"
import { Button, Field } from "../ui/primitives.js"
import { useNotify } from "../ui/toast.js"

interface Props {
  mode: "create" | "edit"
  org: string
  team?: TeamDto
  onClose: () => void
  onSaved: (team: TeamDetailDto) => void
}

const NAME_MIN = 2
const NAME_MAX = 255

export function TeamFormDrawer({ mode, org, team, onClose, onSaved }: Props) {
  const notify = useNotify()
  const [name, setName] = useState(team?.name ?? "")
  const [description, setDescription] = useState(team?.description ?? "")
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const nameValid = name.trim().length >= NAME_MIN && name.length <= NAME_MAX
  const canSubmit = nameValid && !submitting

  async function submit() {
    setSubmitting(true)
    setFormError(null)
    try {
      const saved =
        mode === "create"
          ? await adminApi.createTeam({
              name: name.trim(),
              description: description.trim() || null,
              organizationId: org
            })
          : await adminApi.updateTeam((team as TeamDto).id, {
              name: name.trim(),
              description: description.trim() || null
            })
      notify.success(fr.app.save)
      onSaved(saved)
      onClose()
    } catch (err) {
      if (mode === "create" && isDuplicateError(err)) {
        setFormError(fr.teams.create.errorDuplicate)
      } else {
        setFormError(messageForError(err, fr.teams.create.errorGeneric))
      }
    } finally {
      setSubmitting(false)
    }
  }

  const title = mode === "create" ? fr.teams.create.title : fr.teams.edit.title
  const submitLabel = mode === "create" ? fr.teams.create.submit : fr.teams.edit.submit

  return (
    <Drawer
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {fr.app.cancel}
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <Field label={fr.teams.create.nameLabel} htmlFor="team-name">
        <input
          id="team-name"
          className="field__input"
          value={name}
          maxLength={NAME_MAX}
          placeholder={fr.teams.create.namePlaceholder}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>

      <Field label={fr.teams.create.descriptionLabel} htmlFor="team-desc">
        <textarea
          id="team-desc"
          className="field__textarea"
          value={description}
          maxLength={2000}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>

      {formError ? <div className="field__error">{formError}</div> : null}
    </Drawer>
  )
}
